/**
 * Cookie-based authentication utilities for Happy Alien SSO
 * Handles secure cookie management across *.happyalien.ai domains
 */

export interface AuthCookieOptions {
  domain?: string;
  secure?: boolean;
  sameSite?: "Strict" | "Lax" | "None";
  maxAge?: number; // in seconds
  httpOnly?: boolean;
}

export interface AuthSession {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user: {
    id: string;
    email: string;
    [key: string]: any;
  };
}

const COOKIE_NAME = "ha_token";
const REFRESH_COOKIE_NAME = "ha_refresh";
const DEFAULT_DOMAIN = ".happyalien.ai";

/**
 * Set authentication cookie across Happy Alien domains
 */
export function setAuthCookie(
  session: AuthSession,
  options: AuthCookieOptions = {}
): void {
  // Detect localhost for development
  const isLocalhost = typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  
  const defaultOptions: AuthCookieOptions = {
    domain: isLocalhost ? undefined : DEFAULT_DOMAIN, // No domain for localhost
    secure: !isLocalhost, // HTTP is fine for localhost
    sameSite: "Lax",
    maxAge: 7 * 24 * 60 * 60, // 7 days
    httpOnly: false, // Allow JavaScript access for client-side auth
  };

  const cookieOptions = { ...defaultOptions, ...options };

  // Set access token cookie
  const accessTokenValue = btoa(
    JSON.stringify({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
      expires_at: session.expires_at,
      user_id: session.user.id,
      email: session.user.email,
    })
  );

  setCookie(COOKIE_NAME, accessTokenValue, cookieOptions);

  // Set refresh token cookie (httpOnly for security)
  const refreshTokenValue = btoa(
    JSON.stringify({
      refresh_token: session.refresh_token,
      user_id: session.user.id,
    })
  );

  setCookie(REFRESH_COOKIE_NAME, refreshTokenValue, {
    ...cookieOptions,
    httpOnly: true,
    maxAge: 30 * 24 * 60 * 60, // 30 days
  });
}

/**
 * Get authentication data from cookie
 */
export function getAuthFromCookie(): {
  access_token: string | null;
  user_id: string | null;
  expires_at: number | null;
} {
  try {
    const cookieValue = getCookie(COOKIE_NAME);
    if (!cookieValue) {
      return { access_token: null, user_id: null, expires_at: null };
    }

    const decoded = JSON.parse(atob(cookieValue));

    // Support both new format (access_token) and legacy format (token)
    const accessToken = decoded.access_token || decoded.token;
    const expiresAt = decoded.expires_at || decoded.expires;

    // Check if token is expired
    if (expiresAt && Date.now() / 1000 > expiresAt) {
      clearAuthCookies();
      return { access_token: null, user_id: null, expires_at: null };
    }

    return {
      access_token: accessToken,
      user_id: decoded.user_id,
      expires_at: expiresAt,
    };
  } catch (error) {
    console.error("Error parsing auth cookie:", error);
    clearAuthCookies();
    return { access_token: null, user_id: null, expires_at: null };
  }
}

/**
 * Get refresh token from cookie (server-side only)
 */
export function getRefreshTokenFromCookie(): string | null {
  try {
    const cookieValue = getCookie(REFRESH_COOKIE_NAME);
    if (!cookieValue) return null;

    const decoded = JSON.parse(atob(cookieValue));
    // Support both new format (refresh_token) and legacy format (token)
    return decoded.refresh_token || decoded.token;
  } catch (error) {
    console.error("Error parsing refresh cookie:", error);
    return null;
  }
}

/**
 * Clear all authentication cookies
 */
export function clearAuthCookies(): void {
  deleteCookie(COOKIE_NAME, { domain: DEFAULT_DOMAIN });
  deleteCookie(REFRESH_COOKIE_NAME, { domain: DEFAULT_DOMAIN });
}

/**
 * Check if user is authenticated based on cookie
 */
export function isAuthenticated(): boolean {
  const auth = getAuthFromCookie();
  return auth.access_token !== null;
}

/**
 * Get user ID from cookie without validating token
 */
export function getUserIdFromCookie(): string | null {
  const auth = getAuthFromCookie();
  return auth.user_id;
}

// Helper functions for cookie manipulation
function setCookie(
  name: string,
  value: string,
  options: AuthCookieOptions
): void {
  let cookieString = `${name}=${encodeURIComponent(value)}`;

  if (options.domain) {
    cookieString += `; Domain=${options.domain}`;
  }

  if (options.maxAge) {
    const expires = new Date(Date.now() + options.maxAge * 1000);
    cookieString += `; Expires=${expires.toUTCString()}`;
  }

  if (options.secure) {
    cookieString += "; Secure";
  }

  if (options.sameSite) {
    cookieString += `; SameSite=${options.sameSite}`;
  }

  if (options.httpOnly) {
    cookieString += "; HttpOnly";
  }

  cookieString += "; Path=/";

  if (typeof document !== "undefined") {
    document.cookie = cookieString;
  }
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const nameEQ = name + "=";
  const ca = document.cookie.split(";");

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }

  return null;
}

function deleteCookie(
  name: string,
  options: Pick<AuthCookieOptions, "domain"> = {}
): void {
  setCookie(name, "", {
    ...options,
    maxAge: -1,
  });
}

/**
 * Initialize authentication on page load
 * Returns session data if valid cookie exists
 */
export async function initializeAuth(
  supabaseClient: any
): Promise<AuthSession | null> {
  const auth = getAuthFromCookie();

  if (!auth.access_token) {
    return null;
  }

  try {
    // For client-side initialization, we can use getUser with the access token
    // This validates the token and returns the user data without needing the refresh token
    const { data: { user }, error } = await supabaseClient.auth.getUser(auth.access_token);

    if (error) {
      console.error("Error validating session:", error);
      clearAuthCookies();
      return null;
    }

    if (!user) {
      console.warn("No user returned from token validation");
      clearAuthCookies();
      return null;
    }

    // Construct a session-like object for compatibility
    const session: AuthSession = {
      access_token: auth.access_token,
      refresh_token: '', // Empty for client-side usage
      expires_at: auth.expires_at || 0,
      user: {
        id: user.id,
        email: user.email || '',
        ...user
      }
    };

    return session;
  } catch (error) {
    console.error("Error initializing auth:", error);
    clearAuthCookies();
    return null;
  }
}

/**
 * Handle authentication state changes
 * Updates cookies when session changes
 */
export function handleAuthStateChange(
  event: string,
  session: AuthSession | null
): void {
  switch (event) {
    case "SIGNED_IN":
      if (session) {
        setAuthCookie(session);
      }
      break;

    case "SIGNED_OUT":
      clearAuthCookies();
      break;

    case "TOKEN_REFRESHED":
      if (session) {
        setAuthCookie(session);
      }
      break;

    default:
      break;
  }
}

/**
 * Redirect to login if not authenticated
 */
export function requireAuth(
  loginUrl: string = "https://auth.happyalien.ai/login"
): void {
  if (!isAuthenticated()) {
    const currentUrl = encodeURIComponent(window.location.href);
    window.location.href = `${loginUrl}?redirect=${currentUrl}`;
  }
}
